import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Loader2, ListTodo, Plus, ShieldCheck, ShieldAlert } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { useApp } from "../../../context/AppContext";
import { Panel, Denied } from "./V3Header";
import { T } from "../../../lib/i18n";
import { TodoItem } from "./todo/TodoItem";
import { TodoAddDialog } from "./todo/TodoAddDialog";
import { TodoBoard } from "./todo/TodoBoard";

// 设计待办的「今天」是洛杉矶工作日（后端 design_todo._today() 同为 LA）；
// 用浏览器本地日期的话，中国早上（LA 还是前一天）会把今天的待办全算成逾期、当天列表反而空。
const today = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles" }).format(new Date());

/* 「待办任务」页内的今日代办页签（board=true 时为经理团队看板）：设计师无需进项目即可上传 / 完成上交；全部完成后方可下班打卡 */
export function TodoTab({ v3, board }) {
  const { api, user } = useApp();
  // 必须解构出 setSp：下方 effect 用它把 date 写回地址栏（board 看板按日期切换）。
  // 提交 27484c24 曾把这里误改成 `const [sp]`，导致整个「待办任务」页抛
  // "setSp is not defined" 并整块渲染失败 —— 改动相邻代码时别顺手删掉这个解构。
  const [sp, setSp] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [d, setD] = useState(null);
  const [add, setAdd] = useState(false);
  const [date, setDate] = useState(sp.get("date") || today());
  const loadMeta = useCallback(() => api.get("/design/todo/meta").then(({ data }) => setMeta(data)).catch(() => setMeta({ error: true })), [api]);
  const load = useCallback(() => api.get("/design/todo/my", { params: { date: today() } }).then(({ data }) => setD(data)).catch(() => setD({ today: [], overdue: [], upcoming: [], done: [], gate: {} })), [api]);
  // 单条操作（完成上交 / 本人延期）会顺带回传最新门禁。若让整页重拉，卡片会闪一下白；
  // 这里只把 gate 就地替换 —— 用户刚延期完，最关心的是「还需要几项才能打卡」立刻变化。
  const patchGate = useCallback((res) => { if (res?.gate) setD((prev) => (prev ? { ...prev, gate: res.gate } : prev)); }, []);
  useEffect(() => { loadMeta(); load(); }, [loadMeta, load]);
  useEffect(() => { const n = new URLSearchParams(sp); if (board) n.set("date", date); else n.delete("date"); setSp(n, { replace: true }); }, [board, date]); // eslint-disable-line
  if (!meta) return <div className="h-64 rounded-lg bg-white border border-slate-200 animate-pulse" />;
  if (meta.error) return <Denied status={meta?.status} />;
  const g = d?.gate || {};
  const mergedMeta = { ...meta, discStatusMeta: v3?.discStatusMeta };
  // 四段：逾期（红·拦打卡）/ 今日（黑·拦打卡）/ **进行中**（蓝·未到期，只提示不拦打卡）/ 已上交
  const sections = d ? [["overdue", T("逾期未完成"), d.overdue, "text-red-600"], ["today", T("今日代办"), d.today, "text-slate-800"], ["upcoming", T("未来 7 天（不拦打卡）"), d.upcoming, "text-sky-600"], ["done", T("今日已上交"), d.done, "text-emerald-600"]] : [];
  return (
    <div className="space-y-4" data-testid="design-todo-page">
      {board && meta.isManager ? <TodoBoard api={api} date={date} setDate={setDate} me={user.username} /> : (
        <>
          <div className="flex flex-wrap items-center gap-2 bg-white rounded-lg border border-slate-200 p-3">
            <p className="text-[11.5px] text-slate-500 flex-1 min-w-[200px]">{T("设计部经理在项目交付物上「设为今日代办」或您自行添加 → 在此直接上传交付物「完成上交」（交付物自动「已提交」）· 全部完成后方可下班打卡")}<span className="block mt-0.5 text-slate-400">{T("此处只列交付物代办；日常待办在顶部「每日待办」里（同一张待办表，已同步）")}</span></p>
            {meta.canSelfAdd && <Button size="sm" data-testid="todo-add-btn" onClick={() => setAdd(true)}><Plus className="h-4 w-4 mr-1" />{meta.isManager ? T("派发代办") : T("添加代办")}</Button>}
          </div>
          {g.applies && (
            <div className={`rounded-lg border p-3 flex items-center gap-3 ${g.blocked ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"}`} data-testid="todo-gate-banner" data-blocked={g.blocked ? "1" : "0"}>
              {g.blocked ? <ShieldAlert className="h-6 w-6 text-red-500 shrink-0" /> : <ShieldCheck className="h-6 w-6 text-emerald-500 shrink-0" />}
              <div className="text-sm"><p className={`font-bold ${g.blocked ? "text-red-700" : "text-emerald-700"}`}>{g.blocked ? T("还有 {0} 项今日代办未完成（含逾期），完成前无法下班打卡", [g.pending]) : T("今日代办已全部完成，可以下班打卡")}</p><p className="text-[11px] text-slate-500">{T("如确有原因无法完成，请联系设计部经理改期或取消。")}</p></div>
            </div>
          )}
          {!d ? <div className="py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : sections.map(([k, label, items, cls]) => (
            (items.length > 0 || k === "today") && (
              <Panel key={k} tid={`todo-section-${k}`} title={<span className={cls}>{label} <span className="text-slate-400 font-normal">{items.length}</span></span>} icon={ListTodo}>
                {items.length === 0 ? <p className="text-sm text-slate-400 text-center py-6" data-testid="todo-today-empty">{T("今日暂无代办 · 点击右上角「添加代办」从您负责的项目交付物中选择")}</p>
                  : <div className="grid md:grid-cols-2 gap-2">{items.map((t) => <TodoItem key={t.id} t={t} api={api} isManager={meta.isManager} me={user.username} onChanged={(res) => { patchGate(res); load(); }} />)}</div>}
              </Panel>
            )
          ))}
        </>
      )}
      <TodoAddDialog api={api} open={add} onClose={() => setAdd(false)} onCreated={() => load()} meta={mergedMeta} />
    </div>
  );
}

/* 旧地址 /design/todo → 并入「待办任务」 */
export default function DesignTodo() {
  const [sp] = useSearchParams();
  const tab = sp.get("tab") === "board" ? "board" : "todo";
  return <Navigate to={`/design/tasks?tab=${tab}${sp.get("date") ? `&date=${sp.get("date")}` : ""}`} replace />;
}
